import { EmptyChartState } from "@/components/common/EmptyChartState";
import type { BreachRateByCarrierPoint } from "@/types";
import { Divider, Box, Typography } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useAppTheme } from "@/hooks/useAppTheme";
import { getChartTooltipStyle } from "../utils/chartStyles";

interface BreachRateByCarrierChartProps {
  data: BreachRateByCarrierPoint[];
}

export const BreachRateByCarrierChart = ({ data }: BreachRateByCarrierChartProps) => {
  const { theme } = useAppTheme();

  if (data.length === 0) {
    return <EmptyChartState />;
  }

  const yAxisWidth = Math.max(100, Math.max(...data.map((d) => d.carrier_name.length)) * 7);

  const chartData = data.map((d) => {
    if (d.breached_count > d.total_count) {
      console.warn(
        `BreachRateByCarrierChart: breached_count (${d.breached_count}) > total_count (${d.total_count}) for carrier "${d.carrier_name}"`
      );
    }
    return {
      carrier: d.carrier_name,
      breached: d.breached_count,
      notBreached: Math.max(0, d.total_count - d.breached_count),
    };
  });

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.warning.main,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">Breached</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.primary.main,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">Not Breached</Typography>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
          <YAxis
            type="category"
            dataKey="carrier"
            width={yAxisWidth}
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
          />
          <XAxis type="number" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} allowDecimals={false} />
          <Tooltip
            content={({ payload }) => {
              if (!payload || !payload.length) return null;
              const item = payload[0].payload;
              const total = item.breached + item.notBreached || 1;
              return (
                <div style={{ ...getChartTooltipStyle(theme), padding: "8px" }}>
                  <strong>{item.carrier}</strong>
                  <Divider sx={{ mb: 1, mt: 1 }} />
                  <div>Breached: {item.breached}</div>
                  <div>Not Breached: {item.notBreached}</div>
                  <div>Breach Rate: {((item.breached / total) * 100).toFixed(1)}%</div>
                </div>
              );
            }}
          />
          <Bar dataKey="breached" name="Breached" stackId="a" fill={theme.palette.warning.main} />
          <Bar dataKey="notBreached" name="Not Breached" stackId="a" fill={theme.palette.primary.main} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
